import type { FieldConfig, CustomField, FieldOrigin } from '@gat-ship/types'
import type { FieldConfigOverrides, ResolvedCustomField } from './types'

/**
 * The role context required to resolve field visibility/editability.
 * Threaded as plain data so the resolution core stays pure — the composable
 * unwraps the reactive `userService` values and passes them in.
 */
export interface FieldResolutionContext {
  userRoleIds: readonly string[]
  isTenantAdmin: boolean
}

/** True when the role list is empty or the user holds at least one of the roles. */
export function hasAnyRole(
  roleIds: readonly string[] | null | undefined,
  ctx: FieldResolutionContext
): boolean {
  const ids = roleIds ?? []
  return ids.length === 0 || ids.some((id) => ctx.userRoleIds.includes(id))
}

/** Visibility of a system field for a given config (backend config drives it). */
export function isVisibleForConfig(
  config: FieldConfig | undefined,
  ctx: FieldResolutionContext
): boolean {
  if (!config?.visibilityOption) return true
  if (config.visibilityOption === 'visible') return true
  if (config.visibilityOption === 'hidden') return false
  if (config.visibilityOption === 'visible-for-role') {
    if (ctx.isTenantAdmin) return true
    const roleIds = config.visibleForRoleIds ?? []
    if (roleIds.length === 0) return true
    return roleIds.some((id) => ctx.userRoleIds.includes(id))
  }
  return true
}

/** Editability of a system field for a given config (backend config drives it). */
export function isEditableForConfig(
  config: FieldConfig | undefined,
  ctx: FieldResolutionContext
): boolean {
  if (!config?.editableOption) return true
  if (config.editableOption === 'editable') return true
  if (config.editableOption === 'disabled') return false
  if (config.editableOption === 'editable-for-role') {
    if (ctx.isTenantAdmin) return true
    const roleIds = config.editableForRoleIds ?? []
    if (roleIds.length === 0) return true
    return roleIds.some((id) => ctx.userRoleIds.includes(id))
  }
  return true
}

/** Visibility for a custom field, config overriding the definition (backend wins). */
export function resolveCustomFieldVisible(
  cf: CustomField,
  config: FieldConfig | undefined,
  ctx: FieldResolutionContext
): boolean {
  const visibilityOption = config?.visibilityOption ?? cf.visibilityOption
  if (visibilityOption === 'hidden') return false
  if (visibilityOption === 'visible-for-role' && !ctx.isTenantAdmin) {
    return hasAnyRole(config?.visibleForRoleIds ?? cf.visibleForRoleIds, ctx)
  }
  return true
}

/** Disabled state for a custom field, config overriding the definition. */
export function resolveCustomFieldDisabled(
  cf: CustomField,
  config: FieldConfig | undefined,
  ctx: FieldResolutionContext
): boolean {
  const editableOption = config?.editableOption ?? cf.editableOption
  if (editableOption === 'disabled') return true
  if (editableOption === 'editable-for-role' && !ctx.isTenantAdmin) {
    return !hasAnyRole(config?.editableForRoleIds ?? cf.editableForRoleIds, ctx)
  }
  return false
}

/** Find the config override for a field by name + origin (system by default). */
export function findConfig(
  configs: readonly FieldConfig[],
  fieldName: string,
  origin: FieldOrigin = 'system'
): FieldConfig | undefined {
  return configs.find((c) => c.fieldName === fieldName && c.origin === origin)
}

/**
 * Returns override props to spread onto a gat-form-field-based input.
 * These props are merged with the developer's defaults (backend config wins).
 */
export function fieldProps(
  config: FieldConfig | undefined,
  ctx: FieldResolutionContext
): FieldConfigOverrides {
  if (!config) return {}

  const result: FieldConfigOverrides = {}

  if (config.hint) {
    result.description = config.hint
  }
  if (config.requiredOption === 'required') {
    result.required = true
  }
  if (!isEditableForConfig(config, ctx)) {
    result.readonly = true
  }

  return result
}

/** Whether a named system field is visible (after resolving config + roles). */
export function isSystemFieldVisible(
  configs: readonly FieldConfig[],
  fieldName: string,
  ctx: FieldResolutionContext
): boolean {
  return isVisibleForConfig(findConfig(configs, fieldName, 'system'), ctx)
}

/** All visible custom fields resolved for rendering (sorted, filtered, mapped). */
export function resolveVisibleCustomFields(
  customFieldDefinitions: readonly CustomField[],
  configs: readonly FieldConfig[],
  ctx: FieldResolutionContext
): ResolvedCustomField[] {
  const sorted = [...customFieldDefinitions].sort(
    (a, b) => a.sortOrder - b.sortOrder
  )

  return sorted
    .map((cf) => {
      const config = findConfig(configs, cf.name, 'custom')
      if (!resolveCustomFieldVisible(cf, config, ctx)) return null

      const requiredOption = config?.requiredOption ?? cf.requiredOption

      return {
        fieldName: cf.name,
        label: cf.label,
        inputType: cf.inputType,
        required: requiredOption === 'required',
        disabled: resolveCustomFieldDisabled(cf, config, ctx),
        hint: config?.hint ?? cf.hint,
        aiDescription: cf.aiDescription,
        numberOfDecimals: cf.numberOfDecimals,
        dropdownOptions: cf.dropdownOptions
      } satisfies ResolvedCustomField
    })
    .filter((f): f is ResolvedCustomField => f !== null)
}
